import mitt from 'mitt'
import { v4 as uuidv4 } from 'uuid'
import Appearance from './Appearance'
import Conf from './Conf'
import Survey from './Survey'
import Log from './Log'
import Network from './Network'
import State from './State'
import Storage from './Storage'
import User from './User'
import Livechat from './Livechat'
import I18n from './I18n'
import Modal from './Modal'
import { ObserveVisibility } from 'vue-observe-visibility';

export default class Chatbot {
  async init() {
    this._bus = mitt()

    this.initializeStatus()

    Conf.loadInitialData()
    Log.setLevel(Conf.get().logLevel)
    if (!this.validateConf()) {
      Log.error('Cannot validate configuration.')
      return false
    }

    if (!await this.loadChatbotConfiguration()) {
      Log.error('Cannot load chatbot configuration from config server.')
      return false
    }
    Log.info('Chatbot configuration loaded from config server.')

    Storage.init()
    Log.info('Storage initialized.')

    await Storage.set('session_id', await Storage.get('session_id', uuidv4(), true), true)

    await User.init()
    Log.info('User initialized.')

    await I18n.init()
    Log.info('I18n initialized.')

    await State.init(this._bus)
    this.$state = State
    Log.info('State initialized.')

    Livechat.setup(this._bus)
    Log.info('Livechat initialized.')

    if (State.id() === null) {
      if (await this.canCreateState() === false) {
        Log.info('Chatbot cannot be shown because quota is exceeded.')
      } else {
        Log.info('Waiting for appearance')
        await Appearance.wait()
        await this.setupWindow(true)
      }
    } else {
      await this.setupWindow()
    }
  }

  on(eventType, cb) {
    this._bus.on(eventType, cb)
  }

  async emit(eventType, details = null) {
    await State.append('events', {
      type: eventType,
      date: new Date().toISOString(),
      details
    })
    this._bus.emit(`event:${eventType}`, details)
  }

  validateConf() {
    if (Conf.get().chatbotUri === null) {
      return false
    }
    return true
  }

  initializeStatus() {
    this.status = {
      network: true
    }
  }

  async loadChatbotConfiguration() {
    const ret = await Network.getInstance().get(Conf.get().chatbotUri, {
      domain: window.location.hostname
    })
    if (!ret) {
      return false
    }

    Conf.apply(ret)
    return true
  }

  async canCreateState() {
    const billingReturn = await Network.getInstance().get(`${Conf.get().services.billing}/customers/${Conf.get().customerId}/chatbots/${Conf.get().chatbotId}/state-can-be-created`)
    return !!billingReturn.allowed
  }

  generateColor(Color, base, mode, amount) {
    const color = Color(base)[mode](amount)
    return {
      backgroundColor: color.hex(),
      color: color.isDark() ? '#fff' : '#2D3748'
    }
  }

  async setupWindow() {
    Log.info('Loading Vue components ...')

    const { default: App } = await import(/* webpackChunkName: "chatbot" */ '../chatbot/App.vue')
    const { default: Color } = await import(/* webpackChunkName: "chatbot" */ 'color')
    const { createApp } = await import(/* webpackChunkName: "chatbot" */ 'vue')

    await I18n.loadTranslations()

    let baseMode = Conf.get().home.baseMode ?? 'index'
    let baseOpened = Conf.get().supportDiv !== null || State.id() !== null || Conf.get().forceOpen !== false

    if (Conf.get().forceOpen !== false) {
      baseMode = Conf.get().forceOpen === true ? baseMode : Conf.get().forceOpen
    }
    if (State.id() !== null) {
      baseMode = await State.get('mode', 'index')
      baseOpened = await State.get('opened', true)
    }

    const vue = createApp(App, {
      baseOpened,
      baseMode,
      standalone: Conf.get().supportDiv !== null
    })

    const mainColor = Conf.get().style.colors.main
    const accentColor = Conf.get().style.colors.accent

    let ratio = 0.2
    let colors = null
    while (1) {
      colors = {
        main: {
          lightest: this.generateColor(Color, mainColor, 'lighten', ratio * 3),
          lighter: this.generateColor(Color, mainColor, 'lighten', ratio * 2),
          light: this.generateColor(Color, mainColor, 'lighten', ratio),
          normal: this.generateColor(Color, mainColor, 'darken', 0),
          dark: this.generateColor(Color, mainColor, 'darken', ratio),
          darker: this.generateColor(Color, mainColor, 'darken', ratio * 2),
          darkest: this.generateColor(Color, mainColor, 'darken', ratio * 3)
        },
        accent: {
          lightest: this.generateColor(Color, accentColor, 'lighten', ratio * 3),
          lighter: this.generateColor(Color, accentColor, 'lighten', ratio * 2),
          light: this.generateColor(Color, accentColor, 'lighten', ratio),
          normal: this.generateColor(Color, accentColor, 'darken', 0),
          dark: this.generateColor(Color, accentColor, 'darken', ratio),
          darker: this.generateColor(Color, accentColor, 'darken', ratio * 2),
          darkest: this.generateColor(Color, accentColor, 'darken', ratio * 3)
        }
      }
      if (ratio === 0.05 || colors.main.lightest.backgroundColor !== '#FFFFFF') {
        break
      }
      ratio -= 0.05
    }

    const _this = this

    vue.mixin({
      created() {
        this.$conf = Conf
        this.$state = State
        this.$survey = Survey
        this.$storage = Storage
        this.$log = Log
        this.$livechat = Livechat
        this.$colors = colors
        this.$t = I18n.$t
        this.$i18n = I18n
        this.$bus = _this._bus
        this.$network = Network
        this.$message = params => State.addMessage(params)
        this.$confirm = (question, yesLabel, noLabel) => Modal.confirm(_this._bus, question, yesLabel, noLabel)
      }
    })

    vue.directive('observe-visibility', {
      beforeMount: (el, binding, vnode) => {
        vnode.context = binding.instance;
        ObserveVisibility.bind(el, binding, vnode);
      },
      update: ObserveVisibility.update,
      unmounted: ObserveVisibility.unbind,
    });

    const waitForSupportDiv = _ => {
      Log.info('Looking for support div')
      let supportDiv = null
      if (Conf.get().supportDiv !== null) {
        supportDiv = document.querySelector(Conf.get().supportDiv)
        if (!supportDiv) {
          Log.info('Support div not found, retrying in 1000ms.')
          return false
        }
      }
      if (supportDiv === null) {
        supportDiv = document.createElement('div')
        supportDiv.classList.add('noci-floating')
        supportDiv.classList.add(`noci-position-${Conf.get().style.pin.position}`)
        document.body.appendChild(supportDiv)
      } else {
        supportDiv.classList.add('noci-standalone')
      }
      supportDiv.classList.add('noci-chatbot')
      vue.mount(supportDiv)
      setTimeout(() => {
        this._bus.emit('setupTriggers')
      }, 100)

      Log.info('Vue root component mounted')
      return true
    }

    if (waitForSupportDiv()) {
      return
    }

    const intervalHandler = setInterval(_ => {
      if (waitForSupportDiv()) {
        clearInterval(intervalHandler)
      }
    }, 1000)
  }
}
